import { Button, Toaster } from "@indigo-components";
import { QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, notification } from "antd";
import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
  AuthSessionProvider,
  useAuthSession,
} from "./auth/AuthSessionProvider";
import { UserProvider } from "./auth/UserContext";
import { getCurrentEnv } from "./common/utils";
import {
  DefaultFallbackComponent,
  defaultHandleError,
} from "./error-handling/DefaultFallbackComponent";
import { ConfirmationProvider } from "./hooks/useConfirmation";
import { useInitializeData } from "./hooks/useInitializeData";
import { NotificationModalProvider } from "./hooks/useNotificationModal";
import { ReportGenerationProvider } from "./hooks/useReportGeneration";
import { ServicesProvider } from "./hooks/useServices";
import { useSessionExpiryNotification } from "./hooks/useSessionExpiryNotification";
import { useTheme } from "./hooks/useTheme";
import { queryClient } from "./lib/react-query";
import { GlobalVideoPreview } from "./pages/components/GlobalVideoPreview";
import ScrollToTop from "./pages/components/general/navigation/ScrollToTop";
import MainLayout from "./pages/components/layout/MainLayout";
import { ReferencePdfViewerHost } from "./pages/components/referencePdf/ReferencePdfViewerHost";
import { registerAppointmentEventUnloadFlush } from "./services/appointmentEvents";
import { AuthenticatedFeatureFlagProvider } from "./services/featureFlags";
import { versionCheckService } from "./services/versionCheck";
import { getAntdThemeConfig } from "./theme";
import { AuthUserGroup } from "./types/auth";

function App(): React.JSX.Element {
  return (
    <ConfirmationProvider>
      <NotificationModalProvider>
        <ReportGenerationProvider>
          <InnerApp />
        </ReportGenerationProvider>
      </NotificationModalProvider>
    </ConfirmationProvider>
  );
}

const InnerApp = () => {
  const { userGroup } = useAuthSession();
  useInitializeData(userGroup);
  const [api, contextHolder] = notification.useNotification();

  // Warn before the Cognito refresh token expires — and keep warning through
  // the window after the deadline where the app still looks healthy.
  useSessionExpiryNotification(api);

  // Register the pagehide / visibilitychange:hidden flush for the
  // appointment-event batch queue. Idempotent across re-renders; the
  // service guards against double registration internally.
  useEffect(() => {
    registerAppointmentEventUnloadFlush();
  }, []);

  // Periodically check if there's a new deployment available every hour
  // If there is, show a notification to the user to refresh the page
  useEffect(() => {
    // Don't do version check for doctors or in dev environment
    if (userGroup === AuthUserGroup.DOCTOR || getCurrentEnv() === "dev") {
      return;
    }

    versionCheckService.startPeriodicCheck(60 * 60 * 1000, () => {
      console.log("New version available");

      api.open({
        className: "font-diatype",
        message: "New version available",
        description:
          "Please refresh the page to get the latest features and bug fixes.",
        duration: 0, // 0 means it will stay open until the user closes it
        key: "version-check",
        actions: (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              window.location.reload();
            }}
          >
            Refresh
          </Button>
        ),
        placement: "bottomLeft",
      });
    });

    return () => {
      versionCheckService.stopPeriodicCheck();
    };
  }, [api, userGroup]);

  return (
    <>
      <ScrollToTop />
      <MainLayout />
      <GlobalVideoPreview />
      {/* The one AMA Guide / Rating tables viewer — above every layout branch
          (MainLayout renders /indy alone) so every opener reuses it. */}
      <ReferencePdfViewerHost />
      {contextHolder}
      <Toaster />
    </>
  );
};

// Wrapper component that provides UserProvider after AuthSession is available
const UserProviderWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { authUser } = useAuthSession();

  return <UserProvider authUser={authUser}>{children}</UserProvider>;
};

const AuthenticatedApp = () => {
  const [theme] = useTheme();
  const [themeConfig, setThemeConfig] = useState(() =>
    getAntdThemeConfig(theme),
  );

  useEffect(() => {
    // The theme object is only updated after the data-theme attribute has been updated on the DOM,
    // which is the point at which we can read the new CSS variables.
    setThemeConfig(getAntdThemeConfig(theme));
  }, [theme]);

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={themeConfig} wave={{ disabled: true }}>
        <AuthSessionProvider>
          <ServicesProvider>
            <UserProviderWrapper>
              <AuthenticatedFeatureFlagProvider>
                <App />
              </AuthenticatedFeatureFlagProvider>
            </UserProviderWrapper>
          </ServicesProvider>
        </AuthSessionProvider>
      </ConfigProvider>
    </QueryClientProvider>
  );
};

function AppWithBoundary() {
  return (
    <ErrorBoundary
      FallbackComponent={DefaultFallbackComponent}
      onError={defaultHandleError}
    >
      <AuthenticatedApp />
    </ErrorBoundary>
  );
}

export default AppWithBoundary;
